import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Modal, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { BranchesOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  GitControlPanelModal,
  GitRepoBrowser,
  GitSessionRegistryProvider,
  useGitDraftLeaveGuard,
  useGitSessionRegistryOptional,
  type GitRepoBrowserHandle,
} from '@modeling/git-file-viewer';
import { GitCredentialsSetup } from '../../components/git/GitCredentialsSetup';
import { SchemaGitConnectionForm } from '../../components/schema/SchemaGitConnectionForm';
import { ensureGitFileViewerReady } from '../../bootstrap/gitFileViewer';
import { WORKFLOW_PROFILES_DIR } from '../../config/schemaGit';
import { useDeferredModule } from '../../hooks/useDeferredModule';
import { useGitAuth } from '../../hooks/useGitAuth';
import { useSchemaGitSettings } from '../../hooks/useSchemaGitSettings';
import { workflowProfileService } from '../../api/services';
import { resolveGitApiBaseUrl } from '../../api/env';
import { isGitCredentialNotConfiguredError } from '../../utils/gitErrors';
import type { SchemaGitConnection } from '../../api/schemaProfileApi';
import type { SchemaProfile } from '../../types/schemaProfile';
import type { WorkflowApplyResult } from '../../api/workflowProfileApi';
import '../schema/SchemaProfilesPage.css';

const { Text } = Typography;

function workflowGitPath(profileId: string): string {
  return `${WORKFLOW_PROFILES_DIR}/${profileId}/`;
}

function confirmDiscardDraft(): Promise<boolean> {
  return new Promise((resolve) => {
    Modal.confirm({
      title: '无法保存草稿',
      content: '网络或冲突导致暂存失败。放弃更改并离开？',
      okText: '放弃更改',
      cancelText: '取消',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

function WorkflowProfileEditor({
  profileId,
  repoUrl,
  defaultBranch,
  onCredentialRequired,
  browserRef,
  onDirtyChange,
}: {
  profileId: string;
  repoUrl: string;
  defaultBranch: string;
  onCredentialRequired: () => void;
  browserRef: RefObject<GitRepoBrowserHandle | null>;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const baseURL = resolveGitApiBaseUrl();
  const { getToken, commitAuthor } = useGitAuth();
  const registry = useGitSessionRegistryOptional();

  const session = useMemo(
    () => registry?.sessions.find((s) => s.repoUrl === repoUrl) ?? null,
    [registry?.sessions, repoUrl],
  );
  const currentBranch = session?.branch ?? defaultBranch;
  const changeCount = useMemo(() => {
    if (!session) return 0;
    const staged = session.changes?.staged?.length ?? 0;
    const unstaged = session.changes?.unstaged?.length ?? 0;
    const conflicts = session.rebase?.conflictFiles?.length ?? 0;
    return staged + unstaged + conflicts;
  }, [session]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<WorkflowApplyResult | null>(null);

  const runApply = useCallback(
    async (dryRun: boolean) => {
      if (!repoUrl) {
        message.error('请填写 Git 仓库 URL');
        return null;
      }
      if (!dryRun && changeCount > 0) {
        message.warning('请先通过「源代码管理」提交并同步变更，再执行工作流同步');
        return null;
      }
      return workflowProfileService.apply({
        repoUrl,
        schemaProfile: profileId,
        branch: currentBranch,
        dryRun,
      });
    },
    [changeCount, currentBranch, profileId, repoUrl],
  );

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const result = await runApply(true);
      if (!result) return;
      setPreviewResult(result);
      setPreviewOpen(true);
    } catch (err) {
      if (isGitCredentialNotConfiguredError(err)) {
        onCredentialRequired();
        return;
      }
      message.error(err instanceof Error ? err.message : '预览工作流失败');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const result = await runApply(false);
      if (!result) return;
      const created = result.results.filter((row) => row.action === 'create').length;
      const updated = result.results.filter((row) => row.action === 'update').length;
      const failed = result.results.filter((row) => row.action === 'error').length;
      const tenantLabel =
        result.tenants[0]?.name || result.tenants[0]?.code || '当前登录租户';
      if (failed > 0) {
        message.warning(
          `工作流同步完成：新建 ${created}、更新 ${updated}、失败 ${failed}（当前租户 ${tenantLabel}）`,
        );
      } else {
        message.success(
          `工作流同步完成：新建 ${created}、更新 ${updated}（当前租户 ${tenantLabel}）`,
        );
      }
      setPreviewOpen(false);
      setPreviewResult(null);
    } catch (err) {
      if (isGitCredentialNotConfiguredError(err)) {
        onCredentialRequired();
        return;
      }
      message.error(err instanceof Error ? err.message : '同步工作流失败');
    } finally {
      setSyncLoading(false);
    }
  };

  if (!repoUrl || !baseURL) {
    return (
      <div className="schema-profiles-editor-placeholder">
        请填写 Git 仓库 URL，并配置 <code>REACT_APP_GIT_API_BASE_URL</code>
      </div>
    );
  }

  return (
    <div className="schema-profiles-editor">
      <div className="schema-profiles-editor-toolbar">
        <Space wrap>
          <Text type="secondary">
            Profile: <code>{profileId}</code>
          </Text>
          <Text type="secondary">
            分支: <code>{currentBranch}</code>
          </Text>
          <Text type="secondary">
            目录: <code>{workflowGitPath(profileId)}</code>
          </Text>
          <GitControlPanelModal
            author={commitAuthor}
            repoUrl={repoUrl}
            branch={currentBranch}
            renderTrigger={({ onClick, changeCount: scmCount }) => (
              <Badge count={scmCount} size="small" offset={[-2, 0]}>
                <Button size="small" icon={<BranchesOutlined />} onClick={onClick}>
                  源代码管理
                </Button>
              </Badge>
            )}
          />
          <Button size="small" loading={previewLoading} onClick={handlePreview}>
            预览工作流
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<SyncOutlined />}
            loading={syncLoading}
            onClick={handleSync}
          >
            同步工作流
          </Button>
        </Space>
        <Button type="link" size="small" onClick={onCredentialRequired}>
          Git 凭据
        </Button>
      </div>

      <div className="schema-profiles-editor-browser git-file-viewer-theme-root">
        <GitRepoBrowser
          ref={browserRef as React.ComponentProps<typeof GitRepoBrowser>['ref']}
          key={`${repoUrl}:${defaultBranch}:${profileId}`}
          repoUrl={repoUrl}
          initialPath={workflowGitPath(profileId)}
          defaultBranch={defaultBranch}
          baseURL={baseURL}
          getToken={getToken}
          author={commitAuthor}
          sessionLabel={`workflow:${profileId}`}
          onDirtyChange={onDirtyChange}
        />
      </div>

      <Modal
        title="工作流同步预览"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        onOk={handleSync}
        okText="确认同步"
        confirmLoading={syncLoading}
        width={720}
        destroyOnHidden
      >
        {previewResult ? (
          <>
            <p>
              将从 <code>{previewResult.ref}</code> 读取{' '}
              <strong>{previewResult.declarationCount}</strong> 条声明（
              <code>{previewResult.codes.join(', ') || '无'}</code>
              ），写入当前登录租户{' '}
              <code>
                {previewResult.tenants[0]?.name ||
                  previewResult.tenants[0]?.code ||
                  previewResult.results[0]?.tenantCode ||
                  '—'}
              </code>
              （绑定 profile <code>{previewResult.schemaProfile}</code>）。
            </p>
            <Table
              size="small"
              rowKey={(row) => `${row.tenantId}:${row.code ?? row.path ?? ''}`}
              pagination={false}
              dataSource={previewResult.results}
              columns={[
                {
                  title: '租户',
                  key: 'tenant',
                  render: (_, row) => row.tenantCode || row.tenantId,
                },
                {
                  title: 'code',
                  dataIndex: 'code',
                  key: 'code',
                  render: (v: string) => <code>{v}</code>,
                },
                {
                  title: '动作',
                  dataIndex: 'action',
                  key: 'action',
                  width: 90,
                  render: (action: string) => {
                    const color =
                      action === 'create'
                        ? 'green'
                        : action === 'update'
                          ? 'blue'
                          : action === 'error'
                            ? 'red'
                            : 'default';
                    const label =
                      action === 'create'
                        ? '创建'
                        : action === 'update'
                          ? '更新'
                          : action === 'error'
                            ? '失败'
                            : '无变更';
                    return <Tag color={color}>{label}</Tag>;
                  },
                },
                {
                  title: '说明',
                  dataIndex: 'error',
                  key: 'error',
                  render: (error: string | null | undefined) =>
                    error ? <Text type="danger">{error}</Text> : '—',
                },
              ]}
            />
          </>
        ) : null}
      </Modal>
    </div>
  );
}

export function WorkflowProfilesPage() {
  const gitReady = useDeferredModule(ensureGitFileViewerReady);
  const navigate = useNavigate();
  const { profileId } = useParams<{ profileId?: string }>();
  const { getToken, commitAuthor } = useGitAuth();
  const baseURL = resolveGitApiBaseUrl();
  const {
    settings,
    draft,
    setDraft,
    applyDraft,
    resetDraftToEnvDefaults,
    connectionChanged,
  } = useSchemaGitSettings();

  const [profiles, setProfiles] = useState<SchemaProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [applyingConnection, setApplyingConnection] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gitAccessOk, setGitAccessOk] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const browserRef = useRef<GitRepoBrowserHandle>(null);
  const [, setDirty] = useState(false);
  const { tryLeave } = useGitDraftLeaveGuard(browserRef, {
    confirmDiscard: confirmDiscardDraft,
  });

  const promptCredentials = useCallback(() => {
    setGitAccessOk(false);
    setCredentialsModalOpen(true);
  }, []);

  const openCredentials = useCallback(async () => {
    const ok = await tryLeave();
    if (!ok) return;
    promptCredentials();
  }, [promptCredentials, tryLeave]);

  const navigateToProfile = useCallback(
    async (id: string) => {
      if (id === profileId) return;
      const ok = await tryLeave();
      if (!ok) return;
      navigate(`/workflow-profiles/${id}`);
    },
    [navigate, profileId, tryLeave],
  );

  const loadProfiles = useCallback(
    async (conn: SchemaGitConnection = settings) => {
      if (!conn.repoUrl.trim()) {
        setProfiles([]);
        setGitAccessOk(false);
        setError('请填写 Git 仓库 URL 并点击「应用」');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await workflowProfileService.list(conn);
        if (!result.ok) {
          setProfiles([]);
          setGitAccessOk(false);
          setError(null);
          promptCredentials();
          return;
        }
        setProfiles(result.profiles);
        setGitAccessOk(true);
      } catch (err) {
        if (isGitCredentialNotConfiguredError(err)) {
          setProfiles([]);
          setGitAccessOk(false);
          setError(null);
          promptCredentials();
          return;
        }
        setProfiles([]);
        setGitAccessOk(false);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [promptCredentials, settings],
  );

  const handleApplyConnection = useCallback(async () => {
    const ok = await tryLeave();
    if (!ok) return;
    const next = applyDraft();
    if (!next) {
      message.error('请填写 Git 仓库 URL');
      return;
    }
    setApplyingConnection(true);
    try {
      await loadProfiles(next);
      message.success('已切换 Git 仓库连接');
    } finally {
      setApplyingConnection(false);
    }
  }, [applyDraft, loadProfiles, tryLeave]);

  const handleCredentialsConfigured = useCallback(() => {
    setCredentialsModalOpen(false);
    void loadProfiles();
  }, [loadProfiles]);

  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (!gitReady || initialLoadDoneRef.current) {
      return;
    }
    initialLoadDoneRef.current = true;
    void loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gitReady]);

  const columns: ColumnsType<SchemaProfile> = [
    {
      title: 'Profile ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <code>{id}</code>,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            void navigateToProfile(record.id);
          }}
        >
          编辑
        </Button>
      ),
    },
  ];

  if (!gitReady) {
    return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  }

  const editor = !gitAccessOk ? (
    <div className="schema-profiles-editor-placeholder">
      <p>无法访问工作流 Git 仓库，请检查或配置 SSH 凭据后重试。</p>
      <Space>
        <Button type="primary" onClick={() => void openCredentials()}>
          配置 Git 凭据
        </Button>
        <Button onClick={() => void loadProfiles()} loading={loading}>
          重试
        </Button>
      </Space>
    </div>
  ) : profileId ? (
    <WorkflowProfileEditor
      key={`${settings.repoUrl}:${settings.branch}:${profileId}`}
      profileId={profileId}
      repoUrl={settings.repoUrl}
      defaultBranch={settings.branch}
      onCredentialRequired={() => {
        void openCredentials();
      }}
      browserRef={browserRef}
      onDirtyChange={setDirty}
    />
  ) : (
    <div className="schema-profiles-editor-placeholder">
      请从左侧选择一个 profile，或先在 Git 仓库 <code>{WORKFLOW_PROFILES_DIR}/</code> 下创建目录。
    </div>
  );

  return (
    <GitSessionRegistryProvider
      baseURL={baseURL}
      getToken={getToken}
      defaultAuthor={commitAuthor}
    >
      <div className="schema-profiles-page">
        <div className="schema-profiles-page-header">
          <div className="schema-profiles-page-header-main">
            <div>
              <h2 className="schema-profiles-page-title">工作流定义</h2>
              <p className="schema-profiles-page-desc">
                从 Git 仓库 <code>{WORKFLOW_PROFILES_DIR}/</code> 发现 profile；编辑 JSON
                后提交同步，写入当前登录租户（schemaProfile 须与该租户绑定一致）。仓库 URL
                与分支与 Schema Profile 页共用（保存在本浏览器）。
              </p>
            </div>
            <SchemaGitConnectionForm
              draft={draft}
              onDraftChange={setDraft}
              onApply={() => void handleApplyConnection()}
              onResetEnvDefaults={resetDraftToEnvDefaults}
              connectionChanged={connectionChanged}
              applying={applyingConnection}
              compact
            />
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void loadProfiles()} loading={loading}>
              刷新列表
            </Button>
            <Button onClick={() => void openCredentials()}>Git 凭据</Button>
          </Space>
        </div>

        <div className="schema-profiles-page-body">
          <div className="schema-profiles-page-list">
            {!gitAccessOk && !loading ? (
              <div className="schema-profiles-list-hint">
                <p>无法加载工作流列表，请配置 Git 凭据或点击重试。</p>
                <Space size="small">
                  <Button type="link" size="small" onClick={() => void openCredentials()}>
                    配置凭据
                  </Button>
                  <Button type="link" size="small" onClick={() => void loadProfiles()}>
                    重试
                  </Button>
                </Space>
              </div>
            ) : error ? (
              <div className="schema-profiles-list-error">{error}</div>
            ) : (
              <Table
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={profiles}
                pagination={false}
                onRow={(record) => ({
                  onClick: () => {
                    void navigateToProfile(record.id);
                  },
                  className:
                    record.id === profileId ? 'schema-profiles-row-selected' : undefined,
                })}
              />
            )}
          </div>
          <div className="schema-profiles-page-editor">{editor}</div>
        </div>
      </div>

      <Modal
        title="配置 Git 凭据"
        open={credentialsModalOpen}
        onCancel={() => setCredentialsModalOpen(false)}
        footer={null}
        width={560}
        destroyOnHidden
      >
        <GitCredentialsSetup
          repoUrl={settings.repoUrl}
          hint={`请保存访问以下仓库的 SSH 私钥：${settings.repoUrl || '（请先填写仓库 URL）'}`}
          onConfigured={handleCredentialsConfigured}
        />
      </Modal>
    </GitSessionRegistryProvider>
  );
}
